import { convexQuery } from "@convex-dev/react-query";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useConvexAuth, useMutation } from "convex/react";
import { Heart } from "lucide-react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { api } from "#/../convex/_generated/api";
import type { Id } from "#/../convex/_generated/dataModel";
import {
	InstallBanner,
	InstallBannerDesktop,
	IOSTipSheet,
} from "#/components/install-prompt";
import type { ListingCardData } from "#/components/listing-card";
import { ListingCard } from "#/components/listing-card";
import { useInstallPrompt } from "#/hooks/useInstallPrompt";
import { useSignInModal } from "#/hooks/useSignInModal";
import { cn } from "#/lib/utils";

export const Route = createFileRoute("/watchlist")({
	component: WatchlistPage,
});

type WatchlistItem = ListingCardData & {
	soldAt?: number;
	expiresAt: number;
	isActive: boolean;
};

function isUnavailable(item: WatchlistItem) {
	return (
		!item.isActive || item.soldAt !== undefined || item.expiresAt < Date.now()
	);
}

function WatchlistPage() {
	const { isLoading, isAuthenticated } = useConvexAuth();
	const { open } = useSignInModal();
	useEffect(() => {
		if (!isLoading && !isAuthenticated) open();
	}, [isLoading, isAuthenticated, open]);
	if (isLoading) return null;
	if (!isAuthenticated) return null;
	return <WatchlistContent />;
}

function WatchlistContent() {
	const { t } = useTranslation("watchlist");
	const { data: items = [] } = useQuery(
		convexQuery(api.watchlist.getWatchlist, {}),
	);
	const remove = useMutation(api.watchlist.removeFromWatchlist);
	const { canPrompt, showIOSTip, promptInstall, dismissInstall } =
		useInstallPrompt();

	const watchlist = items as WatchlistItem[];
	const hasItems = watchlist.length > 0;

	return (
		<div className="page-wrap py-12">
			{hasItems && canPrompt && (
				<>
					<InstallBanner onInstall={promptInstall} onDismiss={dismissInstall} />
					<InstallBannerDesktop
						onInstall={promptInstall}
						onDismiss={dismissInstall}
					/>
				</>
			)}
			<IOSTipSheet open={hasItems && showIOSTip} onDismiss={dismissInstall} />
			<div className="flex flex-col gap-10">
				<div className="flex items-end justify-between gap-4">
					<div>
						<p className="sec-tag">{t("header.tag")}</p>
						<h1 className="display-title text-[clamp(36px,4vw,52px)] leading-none text-foreground">
							{t("header.title")}
						</h1>
					</div>
					{watchlist.length > 0 && (
						<div className="flex items-center gap-2 pb-0.5">
							<span className="inline-flex items-center gap-1.5 text-sm text-muted-foreground">
								<Heart className="size-3.5 fill-rose-400 text-rose-400" />
								{watchlist.length} {t("header.saved")}
							</span>
						</div>
					)}
				</div>

				{watchlist.length === 0 ? (
					<EmptyWatchlist />
				) : (
					<div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
						{watchlist.map((item, i) => {
							const unavailable = isUnavailable(item);
							return (
								<div
									key={item._id}
									className="rise-in relative"
									style={{ animationDelay: `${Math.min(i * 25, 250)}ms` }}
								>
									<ListingCard
										listing={item}
										isSaved
										onSaveToggle={() =>
											remove({ listingId: item._id as Id<"listings"> })
										}
									/>
									{unavailable && <UnavailableBadge />}
								</div>
							);
						})}
					</div>
				)}
			</div>
		</div>
	);
}

function UnavailableBadge() {
	const { t } = useTranslation("watchlist");
	return (
		<div className="pointer-events-none absolute inset-0 rounded-[var(--radius)]">
			<div className="absolute inset-0 rounded-[var(--radius)] bg-background/60 backdrop-blur-[2px]" />
			<div className="absolute inset-x-0 bottom-0 flex justify-center pb-5">
				<span
					className={cn(
						"rounded-full border border-border bg-background px-3 py-1",
						"text-[11px] font-semibold text-muted-foreground",
					)}
				>
					{t("unavailable")}
				</span>
			</div>
		</div>
	);
}

function EmptyWatchlist() {
	const { t } = useTranslation("watchlist");
	return (
		<div className="rounded-[var(--radius)] border border-dashed border-border py-24 flex flex-col items-center text-center">
			<div className="relative mb-5 flex size-16 items-center justify-center rounded-full bg-muted">
				<Heart className="size-7 text-muted-foreground/35" />
			</div>
			<p className="font-semibold text-foreground">{t("empty.title")}</p>
			<p className="mt-1 text-sm text-muted-foreground max-w-xs">
				{t("empty.description")}
			</p>
			<Link
				to="/listings"
				search={{
					sort: "newest",
					q: "",
					discClass: [],
					condition: [],
					color: [],
					shipsTo: [],
					country: [],
					plasticType: [],
					priceMin: "",
					priceMax: "",
					weightMin: "",
					weightMax: "",
					discModelIds: [],
					manufacturerIds: [],
					speedMin: "",
					speedMax: "",
					glideMin: "",
					glideMax: "",
					turnMin: "",
					turnMax: "",
					fadeMin: "",
					fadeMax: "",
					distanceKm: "",
				}}
				className="mt-6 inline-flex items-center rounded-[var(--radius)] bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90"
			>
				{t("empty.browse")}
			</Link>
		</div>
	);
}
